import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { PopoverController } from "@ionic/angular";
import { auth } from "firebase";
import metadata from "libphonenumber-js/metadata.full.json";
import { AuthService } from "../../services/auth.service";
import { FirebaseService } from "../../services/firebase.service";
import { HelperService } from "../../services/helper.service";
import { ChangePasswordComponent } from "../change-password/change-password.component";
const country_data = require("../../../assets/data/countries.json");
// https://github.com/mledoze/countries/tree/master?tab=readme-ov-file
// for item in k:
// res[item["cca2"]] = item["name"]["common"]

@Component({
  selector: "app-forgot-password",
  templateUrl: "./forgot-password.component.html",
  styleUrls: ["./forgot-password.component.scss"],
})
export class ForgotPasswordComponent implements OnInit {
  validations_form_phone: FormGroup;
  validations_form_email: FormGroup;
  captcha: auth.RecaptchaVerifier;
  confirmationResult: auth.ConfirmationResult;

  validation_messages = {
    email: [{ type: "pattern", message: "Enter a valid email." }],
    phone: [{ type: "minlength", message: "Enter a valid phone number." }],
  };
  countryCodes: { country: string; dial_code: string }[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private helperServ: HelperService,
    private fireServ: FirebaseService,
    private popoverCtrl: PopoverController
  ) {}

  ngOnInit() {
    this.countryCodes = Object.keys(metadata.countries)
      .map((country) => ({
        country: country_data[country],
        dial_code: `+${metadata.countries[country][0]}`,
      }))
      .filter((country) => country.country != null)
      .sort((a, b) => {
        return a.country.localeCompare(b.country);
      })
      .sort((x, y) => {
        return x.country == "United States" ? -1 : y.country == "United States" ? 1 : 0;
      });

    this.validations_form_phone = this.formBuilder.group({
      phone: new FormControl("", Validators.compose([Validators.required, Validators.minLength(12)])),
      code: new FormControl("", Validators.required),
      extension: new FormControl(this.countryCodes[0], Validators.compose([Validators.required])),
    });

    this.validations_form_email = this.formBuilder.group({
      email: new FormControl(
        "",
        Validators.compose([Validators.required, Validators.pattern("^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$")])
      ),
    });

    // auth().settings.appVerificationDisabledForTesting = true;

    this.setCaptcha();
  }

  setCaptcha() {
    this.captcha = new auth.RecaptchaVerifier("recaptcha-container", {
      size: "invisible",
      callback: (resp) => {
        console.log("resp: ", resp);
      },
      "expired-callback": (res) => {
        console.log("Expired: ", res);
      },
    });
  }

  async checkProviderSignIn(phone?: string, email?: string): Promise<boolean> {
    const data = await this.fireServ.getAuthMethods({
      "email": email,
      "phone": phone
    });
    if (data.exists) {
      if (data.providers.includes("google.com")) {
        this.helperServ.presentToast("Please try logging in through Google.");
      } else if (data.providers.includes("apple.com")) {
        this.helperServ.presentToast("Please try logging in through Apple.");
      } else if (data.providers.includes("hotmail.com")) {
        this.helperServ.presentToast("Please try logging in through Microsoft.");
      } else {
        return true;
      }
      return false;
    } else {
      this.helperServ.presentToast("Account doesn't exist under that handle.");
      return false;
    }
  }

  async sendLink() {
    const phone = `${this.validations_form_phone.get("extension").value.dial_code}${this.validations_form_phone
      .get("phone")
      .value.replace(/\D/g, "")}`;
    console.log("using phone: ", phone);
    if (await this.checkProviderSignIn(phone, null)) {
      this.confirmationResult = await this.authService.sendSMSCode(phone, this.captcha).catch((err) => {
        console.error(err);
        this.setCaptcha();
        this.helperServ.presentToast(`Error sending code - ${err.message} Please try again.`);
        throw Error(err);
      });
      if (this.confirmationResult.verificationId) {
        this.helperServ.presentToast("Code sent successfully.");
      }
    }
  }

  async verifyCode() {
    try {
      await this.confirmationResult.confirm(this.validations_form_phone.get("code").value);
      await this.popoverCtrl.dismiss();
      const popover = await this.popoverCtrl.create({
        component: ChangePasswordComponent,
        cssClass: "resetPasswordPopover",
      });
      await popover.present();
      return true;
    } catch {
      return false;
    }
  }

  async displayToastMessage(success: boolean, value) {
    let message: string;
    if (success) {
      message = "Check your email for a link to reset your password.";
    } else {
      if (value.email == "") {
        message = 'Enter your email in the line above and then click "New Password". A link will be sent to your email.';
      } else {
        message = "The email address provided is not linked to an account.";
      }
    }

    this.helperServ.presentToast(message, false, true, 5000);
    /* const toast = await this.toastCtrl.create({
      message: message,
      showCloseButton: true,
      position: 'bottom',
      closeButtonText: `Dismiss`,
      duration: 5000
    });
    await toast.present() */
  }

  async forgotPassword() {
    const value = this.validations_form_email.get("email");

    if (await this.checkProviderSignIn(null, value.value)) {
      this.authService.resetPasswordWithEmail(value.value).then(
        async (res) => {
          await this.displayToastMessage(true, value);
          await this.popoverCtrl.dismiss();
        },
        (err) => {
          this.displayToastMessage(false, value);
          console.log(err);
        }
      );
    }
  }

  onPhoneInput(event) {
    let filtered = event.target.value.replace(/\D/g, "");
    if (filtered === this.validations_form_phone.get("phone").value.replace(/\D/g, "")) {
      // if new value cleansed is same as old cleansed, then deleted char must be formatting
      if (filtered.length > 6) {
        filtered = `$${filtered.slice(0, 3)}-${filtered.slice(3, 6)}-${filtered.slice(6, 10)}`;
      } else if (filtered.length > 3) {
        filtered = `${filtered.slice(0, 3)}-${filtered.slice(3)}`;
      } else {
        filtered = `${filtered.slice(0)}`;
      }
    } else if (filtered.length > 5) {
      filtered = `${filtered.slice(0, 3)}-${filtered.slice(3, 6)}-${filtered.slice(6, 10)}`;
    } else if (filtered.length > 2) {
      filtered = `${filtered.slice(0, 3)}-${filtered.slice(3)}`;
    } else {
      filtered = `${filtered.slice(0)}`;
    }
    event.target.value = filtered;
    this.validations_form_phone.get("phone").setValue(filtered);
  }
}
